/**
 * Money and receipts — `docs/visit-page.md` §12, §6 (Board 8; the phone's
 * Board 16 D receipt sheet).
 *
 * Everything on this tab is internal except the customer's **PRICE** (the
 * white card) and the receipts' non-money facts: a role without *Costs &
 * margins* gets the price and the receipts with their amounts left out (§2.3).
 * Receipts post right away, flagged (do-not-substitute 10, D11).
 */
import { z } from 'zod';
import { PAID_WITH, RECEIPT_CATEGORIES } from './values.js';
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
const money = z.string().trim().regex(/^\d+(\.\d{1,2})?$/, 'An amount, like 64.52.');
/** `POST /receipts/:id/approve {reimburse}` — **Approve**, with **Pay <first name> back** as switched. */
export const approveReceiptSchema = z.object({
    version: z.number().int().min(1),
    reimburse: z.boolean(),
});
/** `POST /receipts/:id/reject {reason}` — **Reject** asks why; it writes the reversing cost row (§4.7). */
export const rejectReceiptSchema = z.object({
    version: z.number().int().min(1),
    reason: z.string().trim().min(1, 'Say why.').max(2000),
});
/** `POST /receipts/:id/undo-approval` — *Undo approval* puts it back to *To review* (§12). */
export const undoReceiptApprovalSchema = z.object({
    version: z.number().int().min(1),
});
/**
 * `POST /visits/:id/receipts` — **Add a receipt** (office; the phone's *Add
 * receipt*): the photo, vendor, amount, day, *paid with*, *against* — the same
 * fields the crew app sends (§12), through `CrewPort.addReceipt`. It posts at
 * once and is *To review* (D11).
 */
export const addReceiptSchema = z.object({
    fileId: z.string().uuid().nullish(),
    vendorName: z.string().trim().min(1, 'Name the vendor.').max(200),
    /** **Sent by** — who bought it (`field_receipts.paid_by`); the office person is `entered_by` (V4). */
    sentById: z.string().uuid().nullish(),
    vendorId: z.string().uuid().nullish(),
    amount: money,
    day: z.number().int().min(1).max(366),
    at: z.string().datetime({ offset: true }).nullish(),
    /** *What for* — and, for a mixed receipt, what else was in it. */
    description: z.string().trim().min(1, 'Say what it was for.').max(2000),
    paidWith: z.enum(PAID_WITH),
    cardLast4: z.string().trim().regex(/^\d{4}$/).nullish(),
    /** *Against* — one plan line, or null for *Something else*. */
    against: z.object({ kind: z.enum(['material', 'fee']), id: z.string().uuid() }).nullable(),
    category: z.enum(RECEIPT_CATEGORIES).nullish(),
    /** *Pay <first name> back* — defaults on for *Own money*. */
    reimburse: z.boolean().optional(),
});
//# sourceMappingURL=money.js.map